import ApiService from '@/service'
import { moduleActionContext } from '@/vuex'
import { Policy, Rule, UserFunction } from '@klarna/cbrms-policy-rules-manager-client-ts'
import { defineActions, defineModule, defineMutations } from 'direct-vuex'
import { ActionContext } from 'vuex'

export interface HomeState {
  rules: Rule[]
  rulesLoading: boolean
  ruleDrafts: Rule[]
  ruleDraftsLoading: boolean

  policies: Policy[]
  policyDrafts: Policy[]
  policiesLoading: boolean
  policyDraftsLoading: boolean

  userFunctions: UserFunction[]
  userFunctionsLoading: boolean
  userFunctionsDrafts: UserFunction[]
  userFunctionDraftsLoading: boolean

  teammateNames: string[]
}

export const homeActionContext = (context: any) => moduleActionContext(context, homeModule)

/**
 * Extracts all unique `latest_editor` values from the given list of `items`
 * whose `team_code` matches the one from the authorized user.
 *
 * @param context The action context.
 * @param items A list of items containing at least `latest_editor` and `team_code` properties.
 * @returns A list of unique teammate names.
 */
function getLatestEditorsAsUserTeammateNames(
  context: ActionContext<any, any>,
  items: { team_code: string; latest_editor: string }[]
) {
  const { state, rootState } = homeActionContext(context)
  const namesSet = new Set<string>(state.teammateNames)

  if (rootState.auth.userInfo) {
    const { team_code, team_name } = rootState.auth.userInfo
    namesSet.delete(team_name)

    for (const item of items) {
      if (item.team_code === team_code) {
        namesSet.add(item.latest_editor)
      }
    }
  }

  return Array.from(namesSet)
}

const homeModule = defineModule({
  namespaced: true,

  state: {
    rules: [],
    rulesLoading: false,
    ruleDrafts: [],
    ruleDraftsLoading: false,

    policies: [],
    policyDrafts: [],
    policyDraftsLoading: false,
    policiesLoading: false,

    userFunctions: [],
    userFunctionsDrafts: [],
    userFunctionsLoading: false,
    userFunctionDraftsLoading: false,

    teammateNames: [],
  } as HomeState,

  mutations: defineMutations<HomeState>()({
    // This is for unit test usage, so one can setup test state without being mega-intrusive
    REPLACE(state: HomeState, payload: Partial<HomeState>) {
      const keys = Object.keys(payload) as (keyof HomeState)[]

      keys.forEach((key: keyof HomeState) => {
        state[key] = payload[key] as never
      })
    },

    SET_RULES_LOADING(state: HomeState, loading: boolean) {
      state.rulesLoading = loading
    },

    SET_RULE_DRAFTS_LOADING(state: HomeState, loading: boolean) {
      state.ruleDraftsLoading = loading
    },

    SET_RULES(state: HomeState, payload: Rule[]) {
      state.rules = payload
    },

    SET_RULE_DRAFTS(state: HomeState, payload: Rule[]) {
      state.ruleDrafts = payload
    },

    SET_POLICIES_LOADING(state: HomeState, isLoading: boolean) {
      state.policiesLoading = isLoading
    },

    SET_POLICY_DRAFTS_LOADING(state: HomeState, isLoading: boolean) {
      state.policyDraftsLoading = isLoading
    },

    SET_POLICIES(state: HomeState, payload: Policy[]) {
      state.policies = payload
    },

    SET_POLICY_DRAFTS(state: HomeState, payload: Policy[]) {
      state.policyDrafts = payload
    },

    SET_USER_FUNCTIONS_LOADING(state: HomeState, isLoading: boolean) {
      state.userFunctionsLoading = isLoading
    },

    SET_USER_FUNCTION_DRAFTS_LOADING(state: HomeState, isLoading: boolean) {
      state.userFunctionDraftsLoading = isLoading
    },

    SET_USER_FUNCTIONS(state: HomeState, payload: UserFunction[]) {
      state.userFunctions = payload
    },

    SET_USER_FUNCTION_DRAFTS(state: HomeState, payload: UserFunction[]) {
      state.userFunctionsDrafts = payload
    },

    SET_TEAMMATE_NAMES(state: HomeState, names: string[]) {
      state.teammateNames = names
    },
  }),

  actions: defineActions({
    async GET_RULES(ctx) {
      const { commit } = homeActionContext(ctx)
      commit.SET_RULES_LOADING(true)
      try {
        const { data: rules } = await ApiService.getAllRules()
        const teammateNames = getLatestEditorsAsUserTeammateNames(ctx, rules)
        commit.SET_TEAMMATE_NAMES(teammateNames)
        commit.SET_RULES(rules)
      } finally {
        commit.SET_RULES_LOADING(false)
      }
    },

    async GET_RULE_DRAFTS(ctx) {
      const { commit, rootState } = homeActionContext(ctx)
      commit.SET_RULE_DRAFTS_LOADING(true)
      if (!rootState.auth.userInfo || !rootState.auth.userInfo.team_name) return
      try {
        const { data: rules } = await ApiService.getRuleDrafts(rootState.auth.userInfo.team_name)
        const teammateNames = getLatestEditorsAsUserTeammateNames(ctx, rules)
        commit.SET_TEAMMATE_NAMES(teammateNames)
        commit.SET_RULE_DRAFTS(rules)
      } finally {
        commit.SET_RULE_DRAFTS_LOADING(false)
      }
    },

    async GET_USER_FUNCTIONS(ctx) {
      const { commit } = homeActionContext(ctx) as any
      commit.SET_USER_FUNCTIONS_LOADING(true)
      try {
        const { data: userFunctions } = await ApiService.getAllUserFunctions()
        const teammateNames = getLatestEditorsAsUserTeammateNames(ctx, userFunctions)
        commit.SET_TEAMMATE_NAMES(teammateNames)
        commit.SET_USER_FUNCTIONS(userFunctions)
      } finally {
        commit.SET_USER_FUNCTIONS_LOADING(false)
      }
    },

    async GET_USER_FUNCTION_DRAFTS(ctx, payload) {
      const { commit, rootState } = homeActionContext(ctx)
      commit.SET_USER_FUNCTION_DRAFTS_LOADING(true)
      if (!rootState.auth.userInfo || !rootState.auth.userInfo.team_name) return
      try {
        const { data: userFunctions } = await ApiService.getUserFunctionDrafts(
          rootState.auth.userInfo.team_name,
          payload.policy_id,
          payload.policy_branch
        )
        const teammateNames = getLatestEditorsAsUserTeammateNames(ctx, userFunctions)
        commit.SET_TEAMMATE_NAMES(teammateNames)
        commit.SET_USER_FUNCTION_DRAFTS(userFunctions)
      } finally {
        commit.SET_USER_FUNCTION_DRAFTS_LOADING(false)
      }
    },

    async GET_POLICIES(ctx) {
      const { commit } = homeActionContext(ctx)
      commit.SET_POLICIES_LOADING(true)
      try {
        const { data: policies } = await ApiService.getAllPolicies()
        const teammateNames = getLatestEditorsAsUserTeammateNames(ctx, policies)
        commit.SET_TEAMMATE_NAMES(teammateNames)
        commit.SET_POLICIES(policies)
      } finally {
        commit.SET_POLICIES_LOADING(false)
      }
    },

    async GET_POLICY_DRAFTS(ctx) {
      const { commit, rootState } = homeActionContext(ctx)
      commit.SET_POLICY_DRAFTS_LOADING(true)
      if (!rootState.auth.userInfo || !rootState.auth.userInfo.team_name) return
      try {
        const { data: policies } = await ApiService.getPolicyDrafts(rootState.auth.userInfo.team_name)
        const teammateNames = getLatestEditorsAsUserTeammateNames(ctx, policies)

        commit.SET_POLICY_DRAFTS(
          policies.map(policy => ({
            ...policy,
            has_deployment: (policy as any).stack_id,
          }))
        )
        commit.SET_TEAMMATE_NAMES(teammateNames)
      } finally {
        commit.SET_POLICY_DRAFTS_LOADING(false)
      }
    },
  }),
})

export default homeModule
